'use client'

import {useCallback, useMemo} from "react";
import {useQuery} from "@tanstack/react-query";
import {documentKeys} from "@/hooks/queries/queryKeys";
import {queryClient} from "@/hooks/queries/queryClient";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {getCachedDocumentsByIds} from "@/hooks/queries/stores/documentsStore";
import {Feature, Task} from "@/pb/api/devplan/types/tasks";
import {useShallow} from "zustand/react/shallow";
import {documentToFeature, documentToTask} from "@/app/data/converters";
import {DocumentEntity, DocumentType} from "@/pb/api/devplan/types/documents";

export const useDocument = (documentId: string, companyId: number) => {
  const {fetchDocument} = useBoundStore();
  const queryFn = useCallback(async () => {
    await fetchDocument(documentId, companyId)
    return documentId
  }, [fetchDocument, documentId, companyId])
  const document = useBoundStore(
    s => documentId in s.documents ? s.documents[documentId] : null,
    (oldDoc, newDoc) => {
      return (!newDoc && !oldDoc) || newDoc?.updatedAt === oldDoc?.updatedAt
    }
  );

  const {error, refetch: reload, isPending: loading} = useQuery({
    queryKey: documentKeys.detail(companyId, documentId),
    queryFn,
  })

  return {document, loading, error, reload}
}

export const useCachedDocuments = (documentIds: string[] | undefined): DocumentEntity[] | undefined => {
  return useBoundStore(
    s => getCachedDocumentsByIds(s, documentIds),
    (oldDocs, newDocs) => {
      if (oldDocs === undefined) {
        return newDocs === undefined;
      }
      if (newDocs === undefined) {
        return false
      }
      if (oldDocs.length !== newDocs.length) {
        return false
      }
      for (let i = 0; i < oldDocs.length; i++) {
        const oldDoc = oldDocs[i]
        const newDoc = newDocs[i]
        if (oldDoc.id !== newDoc.id || (newDoc.updatedAt || new Date()) > (oldDoc.updatedAt || new Date())) {
          return false
        }
      }
      return true
    },
  )
}

export const useProjectDocuments = (projectId: string | undefined, companyId: number) => {
  const {fetchProjectDocs} = useBoundStore();
  const queryKey = documentKeys.forProject(companyId, projectId ?? "")
  const queryFn = useCallback(async () => {
    if (!projectId) {
      return Promise.resolve("")
    }
    await fetchProjectDocs(companyId, projectId)
    return projectId
  }, [projectId, fetchProjectDocs, companyId])

  const {data, error, refetch: reload, isPending: loading} = useQuery({
    queryKey,
    queryFn,
    // Refetch every 10 minutes
    staleTime: 10 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  })
  const documents = useCachedProjectDocuments(projectId)
  return {documents: data ? documents : undefined, loading, error, reload}
}

export const useCachedProjectDocuments = (projectId: string | undefined) => {
  const docIds = useBoundStore(useShallow(s => projectId ? s.projectDocs[projectId]: []))
  return useCachedDocuments(docIds)
}

export const useCachedSingletonDoc = (projectId: string | undefined, docType: DocumentType) => {
  const docIds = useBoundStore(useShallow(s => projectId ? s.projectDocs[projectId]: []))
  const allDocs = useCachedDocuments(docIds)
  const docs = allDocs?.filter(d => d.type === docType)
  return (docs?.length ?? 0) > 0 ? docs?.[0] : undefined
}

export function refreshProjectDocument(doc: DocumentEntity) {
  const {id, projectId} = doc
  useBoundStore.setState(s => {
    const docs = s.projectDocs[projectId] || []
    if (docs.includes(id)) {
      return s
    }
    return {...s, projectDocs: {...s.projectDocs, [projectId]: [...docs, id]}}
  })
}

export const useChildDocuments = (parentId: string, companyId: number) => {
  const {fetchChildDocuments} = useBoundStore();
  const queryFn = useCallback(
    async () => fetchChildDocuments(companyId, parentId), [fetchChildDocuments, companyId, parentId])
  const {data, isPending, error, refetch: reload} = useQuery({
    queryKey: documentKeys.byParent(companyId, parentId),
    queryFn,
  })
  const documents = useCachedDocuments(data)
  return {documents, loading: isPending, error, reload}
}

export function refreshChildDocument(doc: DocumentEntity) {
  const {companyId, parentId} = doc
  if (!parentId) {
    return
  }
  const queryKey = documentKeys.byParent(companyId, parentId)
  queryClient.setQueryData(queryKey, (oldData: string[] | undefined) => {
    if (oldData === undefined) {
      // If data was never fetched, we should not be updating it
      return undefined
    }
    if (oldData.includes(doc.id)) {
      return oldData
    }
    return [...oldData, doc.id]
  })
}

export function useCachedFeature(id: string): Feature | undefined {
  const doc = useBoundStore(useShallow(s => s.documents[id]))
  return useMemo(() => doc ? documentToFeature(doc) : undefined, [doc])
}

export function useCachedTask(id: string): Task | undefined {
  const doc = useBoundStore(useShallow(s => s.documents[id]))
  return useMemo(() => doc ? documentToTask(doc) : undefined, [doc])
}